import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
} from "@material-ui/core";
import { PowerSettingsNew, Refresh } from "@material-ui/icons";
import exportacaoService from "@services/exportacaoService";
import Holidays from "date-holidays";
import { green, red } from "@material-ui/core/colors";
import ExportacoesChart from "../components/ExportacoesGraph";

import adminService from "../services/adminService";
import Papa from "papaparse";
import exportacoesCSV from "../utils/data/exportacoes1990_2025.csv";
import MonthlyExportacaoGraph from "../components/MonthlyExportacaoGraph";
import showToast from "../components/Toast";
import { Watermark } from "../components/Dashboard/Contratos";
const hd = new Holidays("BR");

const isDiaUtil = (date) => {
  const day = date.getDay();
  if (day === 0 || day === 6) return false;
  return !hd.isHoliday(date);
};

const countDiasUteis = (startDate, endDate) => {
  let count = 0;
  let current = new Date(startDate);
  while (current <= endDate) {
    if (isDiaUtil(current)) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
};

const parseLocalizedNumber = (str) => {
  if (!str) return null;
  if (typeof str == "number") return str;
  if (typeof str !== "string") return null;

  return Number(str.replace(/\./g, "").replace(",", "."));
};
const parseMesAno = (mesStr) => {
  const [mesNome, ano] = mesStr.split(" ");
  const meses = {
    Janeiro: 0,
    Fevereiro: 1,
    Março: 2,
    Abril: 3,
    Maio: 4,
    Junho: 5,
    Julho: 6,
    Agosto: 7,
    Setembro: 8,
    Outubro: 9,
    Novembro: 10,
    Dezembro: 11,
  };
  return new Date(Number(ano), meses[mesNome]);
};

const ExportacaoPage = () => {
  const [exportacoes, setExportacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ultimoMesCSV, setUltimoMesCSV] = useState(false);
  const [dadosCSV, setDadosCSV] = useState(false);
  const [user, cotacao] = useSelector((states) => [
    states.authReducer.user,
    states.cotacaoReducer,
  ]);
  let shouldSeeExportacao = user.type === "A" || cotacao.exportacoesActive;
  const fetchExportacoes = useCallback(() => {
    setLoading(true);
    exportacaoService
      .getExportacoes()
      .then((res) => setExportacoes(res))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchExportacoes();
  }, [fetchExportacoes]);
  useEffect(() => {
    fetch(exportacoesCSV)
      .then((res) => res.text())
      .then((csvText) => {
        const parsed = Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
        });
        const data = parsed.data;

        // Pega só as últimas 12 linhas
        const ultimas12Linhas = data.slice(-12);

        const mesesValidos = ultimas12Linhas
          .map((row) => row["Mes/Ano"]?.trim())
          .filter((m) => m);

        const ultimoMes =
          mesesValidos.length > 0
            ? mesesValidos[mesesValidos.length - 1]
            : null;

        setDadosCSV(ultimas12Linhas);
        setUltimoMesCSV(ultimoMes);
      });
  }, []);
  const groupedByMes = exportacoes.reduce((acc, curr) => {
    const key = curr.mesAtual;
    if (!acc[key]) acc[key] = [];
    acc[key].push(curr);
    return acc;
  }, {});

  const mesesOrdenados = Object.entries(groupedByMes)
    .filter(([_, list]) => list.length === 3)
    .sort(([mesA], [mesB]) => parseMesAno(mesB) - parseMesAno(mesA));

  let registroCertificados = null;
  let registroEmbarques = null;
  let mesSelecionado = "";

  if (mesesOrdenados.length > 0) {
    const [mes, registros] = mesesOrdenados[0];
    registroCertificados = registros[0];
    registroEmbarques = registros[1];
    mesSelecionado = mes;
  } else if (exportacoes.length >= 2) {
    registroCertificados = exportacoes[0];
    registroEmbarques = exportacoes[1];
    mesSelecionado = registroCertificados.mesAtual;
  }

  const calcularProjecoes = () => {
    if (!registroCertificados) return null;

    const dataEmissaoStr = registroCertificados.data;
    const [dia, mes, ano] = dataEmissaoStr.split("/");
    const dataEmissao = new Date(Number(ano), Number(mes) - 1, Number(dia));
    const mesAnoDate = parseMesAno(mesSelecionado);
    const primeiroDiaMes = new Date(
      mesAnoDate.getFullYear(),
      mesAnoDate.getMonth(),
      1,
    );
    const ultimoDiaMes = new Date(
      mesAnoDate.getFullYear(),
      mesAnoDate.getMonth() + 1,
      0,
    );

    const diasUteisAteEmissao = countDiasUteis(primeiroDiaMes, dataEmissao);
    const diasUteisDoMes = countDiasUteis(primeiroDiaMes, ultimoDiaMes);

    if (diasUteisAteEmissao === 0) return null;

    const t = registroCertificados.totais;

    const projecoesDetalhadas = {
      arabica: (t[4] / diasUteisAteEmissao) * diasUteisDoMes * 0.97,
      conilon: (t[5] / diasUteisAteEmissao) * diasUteisDoMes * 0.97,
      soluvel: (t[6] / diasUteisAteEmissao) * diasUteisDoMes * 0.97,
      total: (t[7] / diasUteisAteEmissao) * diasUteisDoMes * 0.97,
      certificadosEmitidos: {
        arabica: t[4],
        conilon: t[5],
        soluvel: t[6],
        total: t[7],
      },
    };
    return projecoesDetalhadas;
  };

  const projecoes = calcularProjecoes();

  const embarquesAnteriores = exportacoes
    .filter((e) => e.mesAnterior && e.embarquesMesAnterior)
    .map((e) => ({
      data: e.data,
      mesAnterior: e.mesAnterior,
      embarquesMesAnterior: e.embarquesMesAnterior,
      totais: e.totais,
    }));

  const renderTable = (t) => {
    const rows = [
      { label: "Arabica", curr: t[4], daily: t[0], prev: t[8] },
      { label: "Conilon", curr: t[5], daily: t[1], prev: t[9] },
      { label: "Solúvel", curr: t[6], daily: t[2], prev: t[10] },
      { label: "Total", curr: t[7], daily: t[3], prev: t[11] },
    ];

    return (
      <TableContainer
        component={Paper}
        style={{ position: "relative", overflow: "hidden" }}
      >
        <Watermark isSmall down />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tipo</TableCell>
              <TableCell align="right">Acumulado</TableCell>
              <TableCell align="right">Diário</TableCell>
              <TableCell align="right">Mês Anterior</TableCell>
              <TableCell align="right">Variação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ label, curr, daily, prev }) => {
              const variacao =
                typeof curr === "number" &&
                typeof prev === "number" &&
                prev !== 0
                  ? (((curr - prev) / prev) * 100).toFixed(1) + "%"
                  : "–";
              return (
                <TableRow key={label}>
                  <TableCell>{label}</TableCell>
                  <TableCell align="right">
                    {curr?.toLocaleString() ?? "–"}
                  </TableCell>
                  <TableCell align="right">
                    {daily?.toLocaleString() ?? "–"}
                  </TableCell>
                  <TableCell align="right">
                    {prev?.toLocaleString() ?? "–"}
                  </TableCell>
                  <TableCell align="right">
                    {typeof curr === "number" &&
                    typeof prev === "number" &&
                    prev !== 0 ? (
                      <Typography
                        style={{
                          fontWeight: "bold",
                          color: curr >= prev ? green[400] : red[400],
                        }}
                      >
                        {curr >= prev ? "+" : "-"}
                        {Math.abs(((curr - prev) / prev) * 100).toFixed(1)}%
                      </Typography>
                    ) : (
                      "–"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  function putCotacao() {
    setLoading(true);
    return adminService
      .putCotacao({
        exportacoesActive: !cotacao.exportacoesActive,
      })
      .then(() => showToast.success("Configuração Salva!"))
      .catch((e) => showToast.error(e.message))
      .finally(() => setLoading(false));
  }

  const formatarVariacao = (atual, anterior) => {
    if (
      typeof atual !== "number" ||
      typeof anterior !== "number" ||
      anterior === 0
    ) {
      return "–";
    }
    const valor = ((atual - anterior) / anterior) * 100;
    const cor = valor >= 0 ? green[400] : red[400];
    const sinal = valor >= 0 ? "+" : "-";
    return (
      <Typography style={{ fontWeight: "bold", color: cor }}>
        {sinal}
        {Math.abs(valor).toFixed(1)}%
      </Typography>
    );
  };

  return (
    <Container maxWidth="lg">
      <Container maxWidth="md">
        <Box mt={3} display="flex" flexDirection="column" gap={2}>
          <div
            style={{
              gap: 12,
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h6" align="center">
                🌎 Exportações de Café
              </Typography>

              {user.type === "A" && (
                <PowerSettingsNew
                  style={{
                    marginLeft: 8,
                    color: cotacao.exportacoesActive ? "#00cc00" : "red",
                    cursor: `pointer`,
                  }}
                  onClick={() => putCotacao()}
                />
              )}
            </div>
            <IconButton onClick={fetchExportacoes}>
              <Refresh />
            </IconButton>
          </div>

          {loading ? (
            <Typography>Carregando...</Typography>
          ) : !registroCertificados || !registroEmbarques ? (
            <Typography>Nenhum dado disponível.</Typography>
          ) : !shouldSeeExportacao ? (
            <Typography
              variant="subtitle1"
              align="center"
              style={{ marginTop: 40 }}
            >
              Aguardando fechamento do mês.
            </Typography>
          ) : (
            <>
              {/* Certificados */}
              <Box style={{ marginBottom: 4 }}>
                <Box
                  style={{
                    display: "flex",
                    gap: 4,
                    marginBottom: 24,
                    marginTop: 12,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    📦 Embarques de {embarquesAnteriores[0]?.mesAnterior} -
                  </Typography>
                  <Typography variant="subtitle2">
                    {embarquesAnteriores[0]?.embarquesMesAnterior?.toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="subtitle2" gutterBottom>
                  📄 Certificados Emitidos até dia {registroCertificados.data} –{" "}
                  {mesSelecionado}
                </Typography>
                <TableContainer component={Paper}>
                  {renderTable(registroCertificados.totais)}
                </TableContainer>
              </Box>

              {/* Embarques */}
              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  style={{ marginTop: 12 }}
                >
                  📦 Embarques até dia {registroEmbarques.data} –{" "}
                  {mesSelecionado}
                </Typography>
                <TableContainer component={Paper}>
                  {renderTable(registroEmbarques.totais)}
                </TableContainer>
              </Box>

              {/* Projeções */}
              <Box mt={4}>
                <Typography variant="subtitle2">
                  📊 Projeção de Embarque para o mês {mesSelecionado}
                </Typography>
                {projecoes ? (
                  <TableContainer
                    component={Paper}
                    style={{ position: "relative", overflow: "hidden" }}
                  >
                    <Watermark isSmall down />
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ whiteSpace: "nowrap" }}>
                            Tipo
                          </TableCell>
                          <TableCell
                            align="right"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            Projeção
                          </TableCell>
                          <TableCell
                            align="right"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            % do Total
                          </TableCell>
                          <TableCell
                            align="right"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            Mês Anterior(MoM)
                          </TableCell>
                          <TableCell
                            align="right"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            % Variação(MoM)
                          </TableCell>
                          <TableCell
                            align="right"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            Ano Anterior(YoY)
                          </TableCell>
                          <TableCell
                            align="right"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            % Variação(YoY)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {["arabica", "conilon", "soluvel", "total"].map(
                          (tipo) => {
                            const acumulado =
                              projecoes.certificadosEmitidos[tipo];
                            const totalAcumulado =
                              projecoes.certificadosEmitidos.total;
                            const projecao = projecoes[tipo];
                            const percentual = totalAcumulado
                              ? ((acumulado / totalAcumulado) * 100).toFixed(1)
                              : "–";

                            // Mês Anterior
                            let mesAnteriorValor = null;
                            if (
                              embarquesAnteriores.length > 0 &&
                              embarquesAnteriores[0].totais &&
                              dadosCSV.length > 0
                            ) {
                              const dadosMesAnterior =
                                dadosCSV[dadosCSV.length - 1];
                              if (dadosMesAnterior) {
                                const mesAnteriorMesAno =
                                  dadosMesAnterior["Mes/Ano"];
                                const isLastMonth = (() => {
                                  if (!mesAnteriorMesAno) return false;
                                  const [mesAbrev, anoAbrev] =
                                    mesAnteriorMesAno.split("-");
                                  const mesesMap = {
                                    jan: 0,
                                    fev: 1,
                                    mar: 2,
                                    abr: 3,
                                    mai: 4,
                                    jun: 5,
                                    jul: 6,
                                    ago: 7,
                                    set: 8,
                                    out: 9,
                                    nov: 10,
                                    dez: 11,
                                  };
                                  const mesNum =
                                    mesesMap[
                                      mesAbrev?.toLowerCase().replace(/\./g, "")
                                    ];
                                  const anoNum = 2000 + parseInt(anoAbrev, 10);
                                  const now = new Date();
                                  const lastMonthDate = new Date(
                                    now.getFullYear(),
                                    now.getMonth() - 1,
                                    1,
                                  );
                                  return (
                                    mesNum === lastMonthDate.getMonth() &&
                                    anoNum === lastMonthDate.getFullYear()
                                  );
                                })();

                                if (isLastMonth) {
                                  switch (tipo) {
                                    case "arabica":
                                      mesAnteriorValor =
                                        dadosMesAnterior.Arabica;
                                      break;
                                    case "conilon":
                                      mesAnteriorValor =
                                        dadosMesAnterior.Conillon;
                                      break;
                                    case "soluvel":
                                      mesAnteriorValor =
                                        dadosMesAnterior.Soluvel;
                                      break;
                                    case "total":
                                      const arabica = parseLocalizedNumber(
                                        dadosMesAnterior.Arabica,
                                      );
                                      const conilon = parseLocalizedNumber(
                                        dadosMesAnterior.Conillon,
                                      );
                                      const soluvel = parseLocalizedNumber(
                                        dadosMesAnterior.Soluvel,
                                      );
                                      mesAnteriorValor =
                                        arabica + conilon + soluvel;
                                      break;
                                    default:
                                      mesAnteriorValor = null;
                                  }
                                }
                              }
                            }

                            // YoY
                            let yoyValor = null;
                            if (dadosCSV.length > 0 && mesSelecionado) {
                              const dataAtual = parseMesAno(mesSelecionado);
                              const dataAnoPassado = new Date(
                                dataAtual.getFullYear() - 1,
                                dataAtual.getMonth(),
                                1,
                              );

                              const mesesMap = {
                                0: "jan",
                                1: "fev",
                                2: "mar",
                                3: "abr",
                                4: "mai",
                                5: "jun",
                                6: "jul",
                                7: "ago",
                                8: "set",
                                9: "out",
                                10: "nov",
                                11: "dez",
                              };

                              const mesAbrev =
                                mesesMap[dataAnoPassado.getMonth()];
                              const anoAbrev = String(
                                dataAnoPassado.getFullYear(),
                              ).slice(-2);
                              const chaveBusca = `${mesAbrev}-${anoAbrev}`;

                              const dadosAnoAnterior = dadosCSV.find((d) => {
                                const rawKey = d["Mes/Ano"];
                                if (!rawKey) return false;
                                const normalizada = rawKey
                                  .toLowerCase()
                                  .replace(".", "")
                                  .trim();
                                return normalizada === chaveBusca;
                              });

                              if (dadosAnoAnterior) {
                                switch (tipo) {
                                  case "arabica":
                                    yoyValor = parseLocalizedNumber(
                                      dadosAnoAnterior.Arabica,
                                    );
                                    break;
                                  case "conilon":
                                    yoyValor = parseLocalizedNumber(
                                      dadosAnoAnterior.Conillon,
                                    );
                                    break;
                                  case "soluvel":
                                    yoyValor = parseLocalizedNumber(
                                      dadosAnoAnterior.Soluvel,
                                    );
                                    break;
                                  case "total":
                                    const arabica = parseLocalizedNumber(
                                      dadosAnoAnterior.Arabica,
                                    );
                                    const conilon = parseLocalizedNumber(
                                      dadosAnoAnterior.Conillon,
                                    );
                                    const soluvel = parseLocalizedNumber(
                                      dadosAnoAnterior.Soluvel,
                                    );
                                    yoyValor = arabica + conilon + soluvel;
                                    break;
                                  default:
                                    yoyValor = null;
                                }
                              }
                            }

                            return (
                              <TableRow key={tipo}>
                                <TableCell
                                  style={{ textTransform: "capitalize" }}
                                >
                                  {tipo === "total"
                                    ? "Total"
                                    : tipo.charAt(0).toUpperCase() +
                                      tipo.slice(1)}
                                </TableCell>
                                <TableCell align="right">
                                  {Math.round(projecao)?.toLocaleString() ??
                                    "–"}
                                </TableCell>
                                <TableCell align="right">
                                  {typeof percentual === "string"
                                    ? percentual + "%"
                                    : `${percentual}%`}
                                </TableCell>
                                <TableCell align="right">
                                  {mesAnteriorValor?.toLocaleString?.() ?? "–"}
                                </TableCell>
                                <TableCell align="right">
                                  {mesAnteriorValor != null
                                    ? formatarVariacao(
                                        projecao,
                                        parseLocalizedNumber(mesAnteriorValor),
                                      )
                                    : "–"}
                                </TableCell>
                                <TableCell align="right">
                                  {yoyValor?.toLocaleString?.() ?? "–"}
                                </TableCell>
                                <TableCell align="right">
                                  {yoyValor != null
                                    ? formatarVariacao(projecao, yoyValor)
                                    : "–"}
                                </TableCell>
                              </TableRow>
                            );
                          },
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography>Não foi possível calcular a projeção.</Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </Container>
      {shouldSeeExportacao ? <ExportacoesChart projecoes={projecoes} /> : null}
      {shouldSeeExportacao ? (
        <MonthlyExportacaoGraph projecoes={projecoes} />
      ) : null}

      {shouldSeeExportacao ? (
        <Box mt={4} mb={2} textAlign="right">
          <Typography variant="caption" color="textSecondary">
            Fonte: CECAFE
          </Typography>
        </Box>
      ) : null}
    </Container>
  );
};

export default ExportacaoPage;
